import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MessageMarkdown } from '../components/MessageMarkdown';
import { useSharedConversation } from '../hooks/useSharedConversation';

const BORDER = '#e5e7eb';
const PRIMARY = '#0E81FF';

/**
 * Public, no-auth view of a chat conversation. Renders the snapshot returned
 * by /api/v1/shared/:token; identity is stripped server-side.
 *
 * Industry-standard hardening:
 * - `<meta name="robots" content="noindex">` so the page doesn't end up in
 *   Google results even though it's technically reachable without auth.
 * - Render-time route entry: no React provider chain wrapping us means
 *   anonymous viewers don't hit the auth gate.
 */
export const SharedChatPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { data, loading, error } = useSharedConversation(token);

  useEffect(() => {
    // Insert a noindex meta tag so search engines don't index shared chats by
    // default. Mirrors what Notion / Linear / Claude.ai do for shared links.
    const existing = document.head.querySelector('meta[name="robots"]');
    const previous = existing?.getAttribute('content') || null;
    let inserted = false;
    if (existing) {
      existing.setAttribute('content', 'noindex, nofollow');
    } else {
      const m = document.createElement('meta');
      m.setAttribute('name', 'robots');
      m.setAttribute('content', 'noindex, nofollow');
      document.head.appendChild(m);
      inserted = true;
    }
    return () => {
      if (inserted) document.head.querySelector('meta[name="robots"]')?.remove();
      else if (existing && previous != null) existing.setAttribute('content', previous);
    };
  }, []);

  // Reflect the title in the browser tab once data lands.
  useEffect(() => {
    if (!data?.title) return;
    const previous = document.title;
    document.title = `${data.title} · Shared chat`;
    return () => {
      document.title = previous;
    };
  }, [data?.title]);

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f9fafb',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: '#111827',
      }}
    >
      <header
        style={{
          background: '#fff',
          borderBottom: `1px solid ${BORDER}`,
          padding: '16px 24px',
        }}
      >
        <div style={{ maxWidth: 820, margin: '0 auto' }}>
          <div style={{ fontSize: 12, color: '#6b7280', letterSpacing: 0.4 }}>
            SHARED CONVERSATION
          </div>
          <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 700 }}>
            {loading ? 'Loading…' : data?.title || 'Shared chat'}
          </h1>
          {data && (
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>
              Snapshot shared on {new Date(data.shared_at).toLocaleString()}
            </p>
          )}
        </div>
      </header>

      <main style={{ maxWidth: 820, margin: '0 auto', padding: '24px' }}>
        {loading && (
          <div style={{ textAlign: 'center', color: '#6b7280', marginTop: 80 }}>
            Loading conversation…
          </div>
        )}

        {error === 'not_found' && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 80,
              padding: 32,
              background: '#fff',
              borderRadius: 12,
              border: `1px solid ${BORDER}`,
            }}
          >
            <div style={{ fontSize: 40, marginBottom: 8 }}>🔒</div>
            <h2 style={{ margin: 0, fontSize: 18, color: '#111827' }}>
              This shared link is no longer available
            </h2>
            <p style={{ marginTop: 8, color: '#6b7280', fontSize: 14 }}>
              The link may have been revoked or never existed. Ask the owner to
              share it again.
            </p>
          </div>
        )}

        {error === 'error' && (
          <div
            style={{
              textAlign: 'center',
              marginTop: 80,
              padding: 32,
              background: '#fff',
              borderRadius: 12,
              border: `1px solid ${BORDER}`,
            }}
          >
            <div style={{ fontSize: 40, marginBottom: 8 }}>⚠️</div>
            <h2 style={{ margin: 0, fontSize: 18, color: '#111827' }}>
              Couldn't load this shared chat
            </h2>
            <p style={{ marginTop: 8, color: '#6b7280', fontSize: 14 }}>
              Please try again in a moment.
            </p>
          </div>
        )}

        {data && (
          <>
            {data.messages.map((msg, idx) => (
              <div
                key={idx}
                style={{
                  marginBottom: 16,
                  display: 'flex',
                  flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                }}
              >
                <div
                  style={{
                    maxWidth: '85%',
                    padding: '12px 16px',
                    borderRadius:
                      msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                    background: msg.role === 'user' ? PRIMARY : '#fff',
                    color: msg.role === 'user' ? '#fff' : '#111827',
                    fontSize: 14,
                    lineHeight: 1.6,
                    border: msg.role === 'user' ? 'none' : `1px solid ${BORDER}`,
                    minWidth: 0,
                  }}
                >
                  {msg.role === 'assistant' ? (
                    <MessageMarkdown content={msg.content} />
                  ) : (
                    <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>
                  )}
                </div>
              </div>
            ))}

            <footer
              style={{
                marginTop: 32,
                padding: '16px 0',
                textAlign: 'center',
                color: '#9ca3af',
                fontSize: 12,
              }}
            >
              This is a read-only snapshot — new messages added by the owner
              won't appear here.
            </footer>
          </>
        )}
      </main>
    </div>
  );
};
